/**
 * supabaseRealtime.js
 *
 * Helpers para subscrições Supabase Realtime (Fase B.1).
 * Substitui listeners onSnapshot do Firestore gradualmente.
 *
 * Uso:
 *   const unsub = await subscribeToChatMessages({
 *     companyId, chatId,
 *     onInsert: (msg) => ...,
 *     onUpdate: (msg) => ...,
 *     onDelete: (id) => ...,
 *   });
 *   // depois: unsub();
 */

import axios from 'axios';
import { supabase } from '../config/supabase';
import { auth } from '../firebase';
import { getBackendUrl } from './api';
import { logger } from './logger';

const BACKEND_URL = getBackendUrl();

// Cache de UUIDs resolvidos: `${companyId}:${chatId}` → { companyUuid, chatUuid }
const uuidCache = new Map();

/**
 * Cada inscrição Realtime precisa de um channel name único no cliente. Reutilizar
 * o mesmo nome (ex.: wa-chats-<uuid>) enquanto o canal ainda está SUBSCRIBED faz o
 * próximo .channel().on() falhar: "cannot add postgres_changes callbacks after subscribe()".
 */
const makeUniqueChannelName = (base) =>
  `${base}:r${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

/**
 * Resolve UUIDs Supabase (companyUuid e chatUuid) a partir dos IDs Firestore.
 * Cache local para evitar re-lookups.
 */
export const resolveChatUuids = async (companyId, chatId, kind = 'whatsapp') => {
  const key = `${kind}:${companyId}:${chatId || ''}`;
  if (uuidCache.has(key)) return uuidCache.get(key);

  try {
    const idToken = await auth.currentUser?.getIdToken();
    const params = new URLSearchParams({ companyId, kind });
    if (chatId) params.set('chatId', chatId);
    const { data } = await axios.get(
      `${BACKEND_URL}/api/supabase/resolve-chat-uuid?${params.toString()}`,
      { headers: idToken ? { Authorization: `Bearer ${idToken}` } : undefined }
    );
    if (data?.companyUuid && (kind === 'company' || data?.chatUuid)) {
      uuidCache.set(key, data);
    }
    return data || { companyUuid: null, chatUuid: null };
  } catch (err) {
    logger.warn('[supabaseRealtime] Falha ao resolver UUIDs:', err?.message);
    return { companyUuid: null, chatUuid: null };
  }
};

/**
 * Converte uma linha de `whatsapp_messages` do Supabase para o formato
 * que o frontend espera (camelCase, compatível com processMessage do useWhatsAppMessages).
 *
 * Mapeamento inverso do _do_sync_whatsapp_message em supabase_sync.py.
 */
export const normalizeSupabaseMessage = (row) => {
  if (!row) return null;
  const metadata = row.metadata || {};
  // Timestamp: Supabase retorna ISO string → converter para epoch seconds (igual ao Firestore)
  let timestamp = row.timestamp;
  if (typeof timestamp === 'string') {
    timestamp = Math.floor(new Date(timestamp).getTime() / 1000);
  }
  return {
    id: row.firestore_id,
    text: row.message,
    type: row.type || 'text',
    status: row.status || 'sent',
    from: row.sender_id,
    sender: row.sender_id,
    url: row.file_url,
    fileName: row.file_name,
    duration: row.duration,
    transcription: row.transcription,
    reaction: row.reaction,
    timestamp,
    ...metadata,
  };
};

/**
 * Subscribe a novas mensagens de um chat via Supabase Realtime.
 * Retorna uma função unsubscribe.
 *
 * @param {Object} params
 * @param {string} params.companyId  - Firestore company ID
 * @param {string} params.chatId     - Firestore chat ID (ou whatsapp_chats doc id)
 * @param {Function} params.onInsert - callback(normalizedMessage)
 * @param {Function} [params.onUpdate] - callback(normalizedMessage)
 * @param {Function} [params.onDelete] - callback(firestoreId)
 * @returns {Promise<Function>} unsubscribe
 */
export const subscribeToChatMessages = async ({
  companyId,
  chatId,
  onInsert,
  onUpdate,
  onDelete,
}) => {
  if (!supabase) {
    logger.warn('[supabaseRealtime] Supabase client não configurado');
    return () => {};
  }

  const { chatUuid } = await resolveChatUuids(companyId, chatId);
  if (!chatUuid) {
    logger.warn('[supabaseRealtime] chatUuid não resolvido — assinatura não criada', { companyId, chatId });
    return () => {};
  }

  const channel = supabase
    .channel(makeUniqueChannelName(`msgs-${chatUuid}`))
    .on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'whatsapp_messages',
        filter: `chat_id=eq.${chatUuid}`,
      },
      (payload) => {
        try {
          const { eventType, new: newRow, old: oldRow } = payload;
          if (eventType === 'INSERT' && onInsert) {
            onInsert(normalizeSupabaseMessage(newRow));
          } else if (eventType === 'UPDATE' && onUpdate) {
            onUpdate(normalizeSupabaseMessage(newRow));
          } else if (eventType === 'DELETE' && onDelete) {
            onDelete(oldRow?.firestore_id);
          }
        } catch (err) {
          logger.warn('[supabaseRealtime] Erro no handler:', err);
        }
      }
    )
    .subscribe((status) => {
      if (status === 'SUBSCRIBED') {
        logger.log(`[supabaseRealtime] ✅ Inscrito em msgs-${chatUuid}`);
      } else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
        logger.warn(`[supabaseRealtime] ⚠️ Canal msgs-${chatUuid} status:`, status);
      }
    });

  return () => {
    try {
      supabase.removeChannel(channel);
    } catch (e) {
      logger.warn('[supabaseRealtime] Erro ao remover canal:', e);
    }
  };
};

/**
 * Helper genérico: cria um canal postgres_changes filtrado por uma única coluna eq
 * e dispara `onChange()` em qualquer evento. Retorna unsubscribe.
 */
const subscribeGeneric = (channelName, table, filter, onChange) => {
  if (!supabase) return () => {};
  const channel = supabase
    .channel(makeUniqueChannelName(channelName))
    .on(
      'postgres_changes',
      { event: '*', schema: 'public', table, filter },
      (payload) => {
        try { onChange(payload); } catch (err) {
          logger.warn(`[supabaseRealtime] handler ${channelName} erro:`, err);
        }
      }
    )
    .subscribe((status) => {
      if (status === 'SUBSCRIBED') logger.log(`[supabaseRealtime] ✅ ${channelName}`);
      else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
        logger.warn(`[supabaseRealtime] ⚠️ ${channelName}:`, status);
      }
    });
  return () => {
    try { supabase.removeChannel(channel); } catch (e) {}
  };
};

/**
 * Subscreve a mudanças em mensagens de um chat interno (1-to-1) via Supabase Realtime.
 * Dispara onChange() (sem args) — caller deve fazer refetch HTTP.
 */
export const subscribeToInternalChatMessages = async ({ companyId, chatId, onChange }) => {
  const { chatUuid } = await resolveChatUuids(companyId, chatId, 'internal');
  if (!chatUuid) {
    logger.warn('[supabaseRealtime] internal chatUuid não resolvido', { companyId, chatId });
    return () => {};
  }
  return subscribeGeneric(
    `internal-msgs-${chatUuid}`,
    'internal_messages',
    `chat_id=eq.${chatUuid}`,
    onChange
  );
};

/**
 * Busca (one-shot) as mensagens de um chat interno (1-to-1) no Supabase.
 * Substitui o `getDocs` do Firestore no re-fetch (Fase E.6 — desligar leitura FS).
 *
 * Retorna objetos no MESMO formato dos docs do Firestore (campos camelCase
 * `senderId/message/fileUrl/...`) + um `id`, para reaproveitar o parser existente
 * em useChatListeners. Mapeamento inverso de _do_sync_internal_message (supabase_sync.py).
 *
 * @returns {Promise<Array>} lista de mensagens (msg-like) ordenada por timestamp asc
 */
export const fetchInternalChatMessages = async ({ companyId, chatId, limit = 50 }) => {
  if (!supabase) {
    logger.warn('[supabaseRealtime] Supabase client não configurado (internal fetch)');
    return [];
  }
  const { chatUuid } = await resolveChatUuids(companyId, chatId, 'internal');
  if (!chatUuid) {
    logger.warn('[supabaseRealtime] internal chatUuid não resolvido (fetch)', { companyId, chatId });
    return [];
  }
  // Pega as últimas N por timestamp desc e reordena asc (igual ao limitToLast do Firestore).
  const { data, error } = await supabase
    .from('internal_messages')
    .select('firestore_id,sender_id,sender_name,message,type,read,file_url,file_name,file_type,duration,transcription,metadata,timestamp')
    .eq('chat_id', chatUuid)
    .order('timestamp', { ascending: false })
    .limit(limit);
  if (error) {
    logger.warn('[supabaseRealtime] Erro ao buscar mensagens internas:', error.message);
    throw error;
  }
  const rows = (data || []).slice().reverse();
  return rows.map((row) => {
    const meta = row.metadata || {};
    return {
      id: row.firestore_id,
      message: row.message,
      type: row.type || 'text',
      senderId: row.sender_id,
      senderName: row.sender_name,
      read: row.read,
      fileUrl: row.file_url || meta.fileUrl || meta.audioUrl || null,
      fileName: row.file_name || null,
      fileType: row.file_type || null,
      duration: row.duration || null,
      transcription: row.transcription || null,
      // timestamp do Supabase é ISO string — o parser já trata ISO string
      timestamp: row.timestamp,
      operatorName: meta.operatorName || null,
      operatorDepartment: meta.operatorDepartment || null,
      operatorId: meta.operatorId || null,
      operatorDepartmentId: meta.operatorDepartmentId || null,
    };
  });
};

/**
 * Busca (one-shot) as mensagens de um grupo no Supabase.
 * Mapeamento inverso de _do_sync_group_message (supabase_sync.py). Retorna objetos
 * msg-like (camelCase) + `id`, para reaproveitar o parser de grupos em useChatListeners.
 */
export const fetchGroupMessages = async ({ companyId, groupId, limit = 50 }) => {
  if (!supabase) {
    logger.warn('[supabaseRealtime] Supabase client não configurado (group fetch)');
    return [];
  }
  const { chatUuid: groupUuid } = await resolveChatUuids(companyId, groupId, 'group');
  if (!groupUuid) {
    logger.warn('[supabaseRealtime] groupUuid não resolvido (fetch)', { companyId, groupId });
    return [];
  }
  const { data, error } = await supabase
    .from('group_messages')
    .select('firestore_id,sender_id,sender_name,message,type,file_url,file_name,file_type,reaction,transcription,timestamp')
    .eq('group_id', groupUuid)
    .order('timestamp', { ascending: false })
    .limit(limit);
  if (error) {
    logger.warn('[supabaseRealtime] Erro ao buscar mensagens de grupo:', error.message);
    throw error;
  }
  const rows = (data || []).slice().reverse();
  return rows.map((row) => ({
    id: row.firestore_id,
    message: row.message,
    text: row.message,
    type: row.type || 'text',
    senderId: row.sender_id,
    senderName: row.sender_name,
    fileUrl: row.file_url || null,
    fileName: row.file_name || null,
    fileType: row.file_type || null,
    reaction: row.reaction || null,
    transcription: row.transcription || null,
    timestamp: row.timestamp,
  }));
};

/**
 * Subscreve a mudanças em mensagens de um grupo via Supabase Realtime.
 */
export const subscribeToGroupMessages = async ({ companyId, groupId, onChange }) => {
  const { chatUuid: groupUuid } = await resolveChatUuids(companyId, groupId, 'group');
  if (!groupUuid) {
    logger.warn('[supabaseRealtime] groupUuid não resolvido', { companyId, groupId });
    return () => {};
  }
  return subscribeGeneric(
    `group-msgs-${groupUuid}`,
    'group_messages',
    `group_id=eq.${groupUuid}`,
    onChange
  );
};

/**
 * Subscreve a mudanças na lista de whatsapp_chats de uma empresa.
 * Filtra por company_id (UUID); o caller deve refazer fetch e aplicar filtros de status/assignedTo.
 */
export const subscribeToCompanyWhatsappChats = async ({ companyId, onChange }) => {
  const { companyUuid } = await resolveChatUuids(companyId, null, 'company');
  if (!companyUuid) {
    logger.warn('[supabaseRealtime] companyUuid não resolvido', { companyId });
    return () => {};
  }
  return subscribeGeneric(
    `wa-chats-${companyUuid}`,
    'whatsapp_chats',
    `company_id=eq.${companyUuid}`,
    onChange
  );
};

/**
 * Subscreve a mudanças na tabela `contacts` de uma empresa.
 * Cobre mudanças que `whatsapp_chats` não cobre: status (Bot/Arquivados/etc),
 * isBlocked, assignedTo, etc. Usado pra invalidar caches das abas
 * Contatos/Finalizados/Agente IA em tempo real.
 */
export const subscribeToCompanyContacts = async ({ companyId, onChange }) => {
  const { companyUuid } = await resolveChatUuids(companyId, null, 'company');
  if (!companyUuid) {
    logger.warn('[supabaseRealtime] companyUuid não resolvido (contacts)', { companyId });
    return () => {};
  }
  return subscribeGeneric(
    `contacts-${companyUuid}`,
    'contacts',
    `company_id=eq.${companyUuid}`,
    onChange
  );
};

// ═══════════════════════════════════════════════════════════════
// FASE B — Notifications + User Status (substitui RTDB)
// ═══════════════════════════════════════════════════════════════

/**
 * Normaliza uma row da tabela `notifications` para o formato que o frontend espera
 * (compatível com o formato RTDB: { [contactId]: { unreadCount, lastMessage, ... } }).
 */
export const normalizeNotificationRow = (row) => {
  if (!row) return null;
  const md = row.metadata && typeof row.metadata === 'object' ? row.metadata : {};
  const fromMeta = md.lastMessageType || md.last_message_type;
  return {
    contactId: row.contact_firestore_id,
    unreadCount: row.unread_count || 0,
    lastMessage: row.last_message,
    lastMessageType: row.last_message_type || fromMeta || null,
    contactName: row.contact_name,
    status: row.status || null,
    assignedTo: row.assigned_to || null,
    timestamp: row.updated_at ? new Date(row.updated_at).getTime() : Date.now(),
  };
};

/**
 * Converte array de rows do Supabase para o mapa { contactId: data } que useChatListeners espera.
 */
const rowsToNotificationMap = (rows) => {
  const map = {};
  for (const row of rows) {
    const norm = normalizeNotificationRow(row);
    if (norm?.contactId) {
      map[norm.contactId] = norm;
    }
  }
  return map;
};

/**
 * Busca todas as notifications de um usuário (carga inicial, substitui RTDB get()).
 */
export const fetchInitialNotifications = async ({ companyId, userId }) => {
  if (!supabase || !userId) return {};
  try {
    const { companyUuid } = await resolveChatUuids(companyId, null, 'company');
    if (!companyUuid) return {};

    const { data, error } = await supabase
      .from('notifications')
      .select('*')
      .eq('company_id', companyUuid)
      .eq('user_id', userId);

    if (error) {
      logger.warn('[supabaseRealtime] Erro ao buscar notifications:', error.message);
      return {};
    }
    return rowsToNotificationMap(data || []);
  } catch (err) {
    logger.warn('[supabaseRealtime] fetchInitialNotifications falhou:', err?.message);
    return {};
  }
};

/**
 * Subscreve a mudanças em notifications de um usuário via Supabase Realtime.
 * O callback `onChange(notificationMap)` recebe o mapa completo { contactId: data }.
 *
 * @returns {Function} unsubscribe
 */
export const subscribeToNotifications = async ({ companyId, userId, onChange }) => {
  if (!supabase || !userId) return () => {};

  const { companyUuid } = await resolveChatUuids(companyId, null, 'company');
  if (!companyUuid) {
    logger.warn('[supabaseRealtime] companyUuid não resolvido para notifications');
    return () => {};
  }

  let currentMap = {};

  const initialData = await fetchInitialNotifications({ companyId, userId });
  currentMap = initialData;
  if (Object.keys(currentMap).length > 0) {
    try { onChange(currentMap); } catch (e) {
      logger.warn('[supabaseRealtime] notifications onChange erro (initial):', e);
    }
  }

  const channel = supabase
    .channel(makeUniqueChannelName(`notifs-${companyUuid}-${userId}`))
    .on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'notifications',
        filter: `user_id=eq.${userId}`,
      },
      (payload) => {
        try {
          const { eventType, new: newRow, old: oldRow } = payload;
          if (eventType === 'DELETE') {
            const fsId = oldRow?.contact_firestore_id;
            if (fsId && currentMap[fsId]) {
              delete currentMap[fsId];
              onChange({ ...currentMap });
            }
          } else {
            const norm = normalizeNotificationRow(newRow);
            if (norm?.contactId) {
              currentMap[norm.contactId] = norm;
              onChange({ ...currentMap });
            }
          }
        } catch (err) {
          logger.warn('[supabaseRealtime] notifications handler erro:', err);
        }
      }
    )
    .subscribe((status) => {
      if (status === 'SUBSCRIBED') {
        logger.log(`[supabaseRealtime] Inscrito em notifications user=${userId}`);
      } else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
        logger.warn(`[supabaseRealtime] notifications canal status: ${status}`);
      }
    });

  return () => {
    try { supabase.removeChannel(channel); } catch (e) {}
  };
};

/**
 * Subscreve a mudanças em user_status de uma empresa via Supabase Realtime.
 * O callback `onChange(statusMap)` recebe o mapa { userId: { online, typing, typingTo, lastSeen } }.
 *
 * @returns {Function} unsubscribe
 */
export const subscribeToUserStatuses = async ({ companyId, onChange }) => {
  if (!supabase) return () => {};

  const { companyUuid } = await resolveChatUuids(companyId, null, 'company');
  if (!companyUuid) {
    logger.warn('[supabaseRealtime] companyUuid não resolvido para user_status');
    return () => {};
  }

  let currentMap = {};

  try {
    const { data } = await supabase
      .from('user_status')
      .select('*')
      .eq('company_id', companyUuid);

    for (const row of (data || [])) {
      currentMap[row.user_id] = {
        online: row.online || false,
        typing: row.typing || false,
        typingTo: row.typing_to || null,
        lastSeen: row.last_seen ? new Date(row.last_seen).getTime() : null,
      };
    }
    if (Object.keys(currentMap).length > 0) {
      try { onChange(currentMap); } catch (e) {}
    }
  } catch (err) {
    logger.warn('[supabaseRealtime] Erro ao buscar user_status inicial:', err?.message);
  }

  const channel = supabase
    .channel(makeUniqueChannelName(`user-status-${companyUuid}`))
    .on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'user_status',
        filter: `company_id=eq.${companyUuid}`,
      },
      (payload) => {
        try {
          const { eventType, new: newRow, old: oldRow } = payload;
          if (eventType === 'DELETE') {
            const uid = oldRow?.user_id;
            if (uid) {
              delete currentMap[uid];
              onChange({ ...currentMap });
            }
          } else {
            const uid = newRow?.user_id;
            if (uid) {
              currentMap[uid] = {
                online: newRow.online || false,
                typing: newRow.typing || false,
                typingTo: newRow.typing_to || null,
                lastSeen: newRow.last_seen ? new Date(newRow.last_seen).getTime() : null,
              };
              onChange({ ...currentMap });
            }
          }
        } catch (err) {
          logger.warn('[supabaseRealtime] user_status handler erro:', err);
        }
      }
    )
    .subscribe((status) => {
      if (status === 'SUBSCRIBED') {
        logger.log(`[supabaseRealtime] Inscrito em user_status company=${companyId}`);
      } else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
        logger.warn(`[supabaseRealtime] user_status canal status: ${status}`);
      }
    });

  return () => {
    try { supabase.removeChannel(channel); } catch (e) {}
  };
};

/**
 * Subscreve a mudanças em uma conversation específica via Supabase Realtime.
 * Usado pelo ChatSettings.js para sincronizar mensagens da Beny em tempo real.
 *
 * @param {string} conversationFirestoreId - Firestore doc ID da conversation
 * @param {Function} onChange - callback(conversationRow) — row completa da tabela conversations
 * @returns {Function} unsubscribe
 */
export const subscribeToConversation = (conversationFirestoreId, onChange) => {
  if (!supabase || !conversationFirestoreId) return () => {};

  const channel = supabase
    .channel(makeUniqueChannelName(`conv-${conversationFirestoreId}`))
    .on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'conversations',
        filter: `firestore_id=eq.${conversationFirestoreId}`,
      },
      (payload) => {
        try {
          const row = payload.new || payload.old;
          if (row) onChange(row);
        } catch (err) {
          logger.warn('[supabaseRealtime] conversation handler erro:', err);
        }
      }
    )
    .subscribe((status) => {
      if (status === 'SUBSCRIBED') {
        logger.log(`[supabaseRealtime] Inscrito em conversation ${conversationFirestoreId}`);
      }
    });

  return () => {
    try { supabase.removeChannel(channel); } catch (e) {}
  };
};

/**
 * Busca uma conversation pelo firestore_id (carga inicial para ChatSettings).
 */
export const fetchConversationByFirestoreId = async (firestoreId) => {
  if (!supabase || !firestoreId) return null;
  try {
    const { data, error } = await supabase
      .from('conversations')
      .select('*')
      .eq('firestore_id', firestoreId)
      .maybeSingle();
    if (error || !data) return null;
    return data;
  } catch {
    return null;
  }
};

/**
 * Atualiza user_status via backend (escreve no Supabase + RTDB durante dual-write).
 */
export const updateUserStatusViaApi = async ({ companyId, userId, status }) => {
  try {
    const idToken = await auth.currentUser?.getIdToken();
    await axios.post(
      `${BACKEND_URL}/api/user-status`,
      { companyId, userId, ...status },
      { headers: idToken ? { Authorization: `Bearer ${idToken}` } : undefined }
    );
  } catch (err) {
    logger.warn('[supabaseRealtime] Erro ao atualizar user_status:', err?.message);
  }
};
